<template>
  <main class="pp-page--seating-area">
    <h1 class="title title--desktop" v-t="'message.choose_seating_area'"></h1>
    <div class="pp-layout-container">
      <div class="pp-preamble">
        <h1
          v-t="'message.choose_seating_area'"
          class="title title--mobile"
        ></h1>
        <h2 v-t="'message.budget_per_ticket'"></h2>
      </div>
      <section class="pp-main-content">
        <!-- These are the price categories on the screen -->
        <div class="category-container">
          <button v-for="(priceCat, index) in allPriceCategories"
            class="category-price"
            :class="{ 'category-price--active' : index == activeCategoryIndex }"
            :key="index"
            @click="selectPriceCategory(index, priceCat, labelColours[priceCat.relatedID].border)">
            <span class="category-price__symbol"
              :style="{ 'background-color': labelColours[priceCat.relatedID].fill, }"/>
            <span class="category-price__price">{{ priceCat.catPrice }}</span>
          </button>
        </div>
        <!-- This controls the Seating Area map navigation -->
        <div class="image-container" ref="imageContainer">
          <panZoom
            @init="initPanzoom"
            :options="{ minZoom: 0.5, maxZoom: 4, fit: true }">
            <svg ref="imageViewport"
              :viewBox="'0 0 ' + performance.backgroundImageWidth + ' ' + performance.backgroundImageHeight">
              <image ref="backgroundMap" :href="performance.backgroundImageSource" />
              <seat-area-outline
                v-for="(seatLevel, index) in allSeatLevels"
                :key="index"
                :highlight="seatLevel.selected"
                :areaInfo="seatLevel"
                :color="labelColours[seatLevel.priceCategoryNum].border"
                :class="{ 'seat-area-outline--sold-out' : hoveredIsSoldOut }"
                @seatAreaMouseEnter="areaMove"
                @seatAreaMouseLeave="areaBlur"
                @seatAreaClick="selectArea"/>
            </svg>
          </panZoom>
        </div>
        <div class="seating-area__accessibility-disclaimer">
          <p v-if="$i18n.locale == 'tc'">我們樂意按殘疾人士不同需要作出適當安排，請致電<a href="tel:+85222000022">（852）2200 0022</a> 客戶服務熱線或電郵至 <a href="mailto:ticketinfo@wkcda.hk">ticketinfo@wkcda.hk</a>。</p>
          <p v-else-if="$i18n.locale == 'sc'">我们乐意按残疾人士不同需要作出适当安排，请致电<a href="tel:+85222000022">（852）2200 0022</a> 客户服务热线或电邮至 <a href="mailto:ticketinfo@wkcda.hk">ticketinfo@wkcda.hk</a>。</p>
          <p v-else>We strive to make our programmes accessible to all. Please contact our Customer Service Hotline at <a href="tel:+85222000022">(852) 2200 0022</a> or email <a href="mailto:ticketinfo@wkcda.hk">ticketinfo@wkcda.hk</a> for suitable arrangements.</p>
        </div>
        <zoom-controls @zoom="zoom" @resetPosition="resetPosition" />
        <div class="pp-actions">
          <div class="pp-actions__container">
            <error :error="$t('errors.no_seating_area_selected')" v-if="error"/>
          </div>
        </div>
      </section>
      <aside class="pp-sidebar">
        <current-area-info
          v-if="hoveredSectionName"
          :sectionName="hoveredSectionName"
          :sectionPrice="hoveredSectionPrice"
          :sectionAvailability="hoveredSectionAvailability"
        />
        <zoom-controls @zoom="zoom" @resetPosition="resetPosition" />
        <pan-controls @pan="pan" />
      </aside>
    </div>
    <modal-confirm-section
      :sectionInfo="clickedSectionInfo"
      @chooseSeatingArea="chooseSeatingArea"
    />
    <modal-accessible-zone @advisoryConfirmed="advisoryConfirmed" />
  </main>
</template>

<script>
import api from "@/util/api";
import _ from "lodash";
import Error from "@/components/generic/Error";
import CurrentAreaInfo from "@/components/seating-area/CurrentAreaInfo";
import PanControls from "@/components/seating-area/PanControls";
import ZoomControls from "@/components/seating-area/ZoomControls";
import CanvasInteractions from "@/util/CanvasInteractions";
import FancyButton from "@/components/generic/FancyButton";
import ModalConfirmSection from "@/components/modals/ModalConfirmSection";
import SeatAreaOutline from "@/components/SeatAreaOutline";
import ModalAccessibleZone from "@/components/modals/ModalAccessibleZone.vue";
import { mapActions, mapState } from 'vuex';
import Helpers from "@/util/helpers"

export default {
  name: "seating-area",
  mixins: [CanvasInteractions, Helpers],
  components: {
    Error,
    CurrentAreaInfo,
    PanControls,
    ZoomControls,
    FancyButton,
    ModalConfirmSection,
    SeatAreaOutline,
    ModalAccessibleZone,
  },
  data() {
    return {
      // "fill" corresponds to the values found at the following link: https://api.stixcloudtest.com/docs/#2-3-get-event-overview-seat-map
      // "border" same tone but different lightness. In case you need to add another colour, it'll be necessary to modify the lightness (up) to have AA contrast ratio (if possible) https://webaim.org/resources/contrastchecker/
      labelColours: [
        { fill: "#FFFFFF", border: "#000000" },
        { fill: "#FECD84", border: "#834F02" },
        { fill: "#A5C7F5", border: "#124DA1" },
        { fill: "#71BA64", border: "#284521" },
        { fill: "#D097F0", border: "#61168D" },
        { fill: "#F7F19F", border: "#716A0A" },
        { fill: "#B5EDF3", border: "#166D74" },
        { fill: "#D9C297", border: "#644D26" },
        { fill: "#A6F0B9", border: "#13712B" },
        { fill: "#FB9CC0", border: "#93063C" },
        { fill: "#D6CC1B", border: "#55520C" },
        { fill: "#929E92", border: "#2D342D" },
        { fill: "#4AD1B1", border: "#16554B" },
        { fill: "#8C65A0", border: "#312339" },
        { fill: "#A80717", border: "#37060C" },
        { fill: "#FF0049", border: "#38000F" },
        { fill: "#D89D61", border: "#553616" },
        { fill: "#3D7717", border: "#1B3107" },
        { fill: "#AE9FD6", border: "#403073" },
        { fill: "#EC826A", border: "#652010" },
        { fill: "#FFC7CE", border: "#BD0019" },
      ],
      seatingAreas: [],
      seatingAvailability: [],
      priceCategoryIdSelected: -1,
      seatSectionIdSelected: -1,
      seatSectionTypeSelected: -1,
      buttonSelected: "",
      error: false,
      hoveredSectionName: "--",
      hoveredSectionPrice: "--",
      hoveredSectionAvailability: "--",
      hoveredIsSoldOut: false,
      clickedSectionInfo: {
        availability: "",
        price: "",
        sectionName: "",
        levelName: "",
        soldOut: false
      },
      map: {},
      allPriceCategories: [],
      activeCategoryIndex: null,
      allSeatLevels: [],
      MAX_API_ATTEMPTS: 3,
    };
  },
  watch: {
    "$i18n.locale": function() {
      this.initCreated();
    }
  },
  async created() {
    await this.initCreated();
  },
  methods: {
    async initCreated() {
      this.clearBackgroundImage();
      this.clearError();
      this.allPriceCategories = [];
      this.seatingAvailability = []
      this.seatingAreas = []
      this.allSeatLevels = []

      await this.retrieveSeatingAreas().then(async () => {
        await this.retrieveSeatingAvailability();
      });
    },
    async retrieveSeatingAreas() {
      let attempts = 0;

      while (attempts < this.MAX_API_ATTEMPTS) {
        this.updateLoading(true);
        attempts++;

        try {
          let response = await api.getSeatmapOverview(this.performance.productId, this.$i18n.locale);

          if (response.status == 200) {
            attempts = this.MAX_API_ATTEMPTS
            this.updateMode(response.data.data.mode);

            // At this stage we don't know if the Seating Area that is selected is BA/HS as well, or just GA. 
            // seatSectionType in ChooseSeatFlow handles this
            if (this.performance.mode == "BA" || this.performance.mode == "HS") {
              this.updateProgressBarPath({ key: "paidEventNoSeatAllocationSeatMapRequired", value: true });
            } else {
              this.updateProgressBarPath({ key: "standardSeatAllocation", value: true });
            }

            this.seatingAreas = response.data.data.seatSectionList;
            this.map = this.$refs.backgroundMap;

            this.seatingAreas.forEach((item) => {
              let processedCatPrice = this.formattedPrice(item.priceCatAmount.amount)

              this.allPriceCategories.push({
                sectionID: item.priceCatAlias.priceCatAlias,
                catPrice: processedCatPrice,
                relatedID: item.SeatLevel[0].priceCategoryNum,
                priceCategoryId: item.SeatLevel[0].priceCategoryId
              });

              this.allSeatLevels.push(item.SeatLevel);
            });

            this.allSeatLevels = _.flatten(this.allSeatLevels);

            // This then interacts with a bunch of functions /
            // watchers / data variables in CanvasInteractions mixin
            this.setupBackgroundImage(response.data.data.imageURL);

            // NOTE: We set updateLoading false in setupBackgroundImage instead of here
          } else {
            throw response;
          }
        } catch (e) {
          failedToRetrieveSeatAreas(e);
        }
      }

      function failedToRetrieveSeatAreas(e) {
        let errorContent = _.get(e.response.data, 'error.source.content');
        let nonRepeatErrors = [
          'error.product.overview-seatmap.generation.fail'
        ]

        if (nonRepeatErrors.includes(errorContent)) {
          attempts = this.MAX_API_ATTEMPTS;
        }

        if (attempts == this.MAX_API_ATTEMPTS) {
          if(errorContent) {
            // Preferentially use SISTIC error code
            this.updateSisticError(errorContent)
            this.updateFallbackError('generic_error');
          } else {
            this.updateError("seating_area_failed");
          }

          this.triggerGoBack();
        }
      }
    },
    async retrieveSeatingAvailability() {
      let attempts = 0;

      while (attempts < this.MAX_API_ATTEMPTS) {
        this.updateLoading(true);
        attempts++;

        try {
          let response = await api.getSeatAvailabilityForSection(this.performance.productId);

          if (response.status == 200) {
            attempts = this.MAX_API_ATTEMPTS

            this.seatingAvailability = response.data.data;
            this.updateLoading(false);
          } else {
            throw response;
          }
        } catch (e) {
          failedToRetrieveAvailability(e);
        }
      }

      function failedToRetrieveAvailability(e) {
        let errorContent = _.get(e.response.data, 'error.source.content')

        if (attempts == this.MAX_API_ATTEMPTS) {
          if(errorContent) {
            // Preferentially use SISTIC error code
            this.updateSisticError(errorContent)
            this.updateFallbackError('seating_area_failed');
          } else {
            this.updateError("seating_area_failed");
          }

          this.triggerGoBack();
        }
      }
    },
    chooseSeatingArea() {
      if (this.priceCategoryIdSelected != -1 &&this.seatSectionIdSelected != -1) {
        this.error = false;

        this.$emit("chooseArea", {
          priceCategoryId: this.priceCategoryIdSelected,
          seatSectionId: this.seatSectionIdSelected,
          seatSectionName: this.clickedSectionInfo.sectionName,
          seatLevelName: this.clickedSectionInfo.levelName,
          seatSectionType: this.seatSectionTypeSelected,
        });

        this.scrollToTopOfPurchasePath();
      } else {
        this.error = true;
      }
    },
    clearItems() {
      this.setSectionInfo("clear");

      this.allSeatLevels.forEach((level, index) => {
        this.allSeatLevels[index].selected = false;
        level.selected = false;
        this.$set(this.allSeatLevels, index, level);
      });
    },
    highlightCat(category, index) {
      // If index & activeCategory match, then we're just re-enabling the previously highlighted section
      if (index != this.activeCategory) this.activeCategoryIndex = index;

      this.allSeatLevels.forEach((level, index) => {
        this.allSeatLevels[index].selected = false;

        let categoryRelatedID = _.get(category, "relatedID", false);

        if (level.priceCategoryNum == categoryRelatedID) {
          level.selected = true;
          this.$set(this.allSeatLevels, index, level);

          this.setSectionInfo("category-selected", {
            name: category.sectionID,
            price: category.catPrice,
          });
        }
      });
    },
    setSectionInfo(context, info) {
      if (context == "clear") {
        this.hoveredSectionName = "--";
        this.hoveredSectionPrice = "--";
        this.hoveredSectionAvailability = "--";
      } else if (context == "category-selected") {
        /*
         * During draw items we're setting name, price, and availability for *each* seating area within a price category
         * This could be dozens, with different values
         * Therefore, we clear availability & set the *category* price & name
         * As otherwise we're using the info from the last area, within the category
         */
        this.hoveredSectionName = info.name;
        this.hoveredSectionPrice = info.price;
        this.hoveredSectionAvailability = "--";
      } else if (context == "draw-items") {
        this.hoveredSectionPrice = this.getPriceCategoryPrice(info.priceCategoryId);
        this.hoveredSectionName = info.seatSectionAlias.seatSectionAlias;
        this.clickedSectionInfo.availability = this.calcAvailability(
          info.seatSectionId,
          info.priceCategoryId
        );
        this.hoveredSectionAvailability = this.clickedSectionInfo.availability;
      }
    },
    areaMove(area) {
      this.clearItems();
      this.setSectionInfo("draw-items", area);
    },
    areaBlur() {
      this.setSectionInfo("clear");

      let categoryToHighlight = this.allPriceCategories[
        this.activeCategoryIndex
      ];

      this.highlightCat(categoryToHighlight, this.activeCategoryIndex);
    },
    getPriceCategoryPrice(priceCategoryId) {
      let price = "--"
      
      this.allPriceCategories.some((item) => {
        if(item.priceCategoryId == priceCategoryId) {
          price = item.catPrice;
          // Exit some: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
          return true;
        }
      })

      return price
    },
    selectPriceCategory(index, category) {
      // We want to clear regardless: we're either switching to a new category or activating/deactivating from no selection
      this.clearItems();
      // deselect the active price category
      if (this.activeCategoryIndex == index) {
        this.activeCategoryIndex = null;
      } else {
        // set this item as the active category
        this.activeCategoryIndex = index;

        this.highlightCat(category, index);
      }
    },
    calcAvailability(seatSectionId, priceCategoryId) {
      let availability = "--";
      let foundPrice = false;

      // Find the availability text to display
      this.seatingAvailability.some((section) => {
        if (seatSectionId == section.seatSectionId && priceCategoryId == section.priceCategoryId) {
          foundPrice = true;
          switch (section.seatSectionAvailability) {
            case 0:
              availability = this.$t('availability.sold_out');
              break;
            case 1:
              availability = this.$t('availability.available');
              break;
            case 2:
              availability = this.$t('availability.limited_seats');
              break;
            case 3:
              availability = this.$t('availability.single_seat');
              break;
          }

          // Exit some: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
          return true;
        }
      });

      if(!foundPrice) {
        this.hoveredIsSoldOut = true;
        availability = this.$t('availability.sold_out');
      } else {
        this.hoveredIsSoldOut = false;
      }

      return availability;
    },
    selectArea(area) {
      this.setSectionInfo("draw-items", area);

      // Need to store this info ahead of moving to ChooseSeats
      // (which is triggered via the confirm section modal)
      this.priceCategoryIdSelected = area.priceCategoryId;
      this.seatSectionIdSelected = area.seatSectionId;
      this.seatSectionTypeSelected = area.seatSectionType;

      // Display info in the modal
      // we could use the 'hovered' info but it's being cleared on mouseleave
      this.clickedSectionInfo.sectionName = area.seatSectionAlias.seatSectionAlias;
      this.clickedSectionInfo.levelName = area.seatLevelAlias.seatLevelAlias
      this.clickedSectionInfo.price = this.getPriceCategoryPrice(area.priceCategoryId);
      this.clickedSectionInfo.soldOut = this.hoveredIsSoldOut

      this.allPriceCategories.some((category) => {
        if (area.priceCategoryNum == category.relatedID) {
          this.showModal(category.sectionID);
          // Exit some: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/some
          return true;
        }
      });
    },
    showModal(sectionID) {
      if (sectionID.includes("Accessible Zone") || sectionID.includes("無障礙區") || sectionID.includes("无障碍区")) {
        this.$modal.show("modal-accessible-zone");
      } else {
        this.$modal.show("modal-confirm-section");
      }
    },
    advisoryConfirmed() {
      this.$modal.show("modal-confirm-section");
    },
    ...mapActions([
      'updateLoading',
      'updateError',
      'clearError',
      'updateMode',
      'setupBackgroundImage',
      'clearBackgroundImage',
      'scrollToTopOfPurchasePath',
      'triggerGoBack',
      'updateSisticError',
      'updateFallbackError',
      'updateProgressBarPath'
    ])
  },
  computed: {
    ...mapState(['performance'])
  }
};
</script>
